/**
 * File: useGlobalContext.ts
 * Description: Hook for managing global context based on authentication and navigation state
 * Author: System
 * Created: 2025-09-04
 * Last Modified: 2025-09-04
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { contextManager } from '../services/contextManager';

// Helper function to extract project context from URL
const extractProjectContext = (pathname: string) => {
  const projectMatch = pathname.match(/\/project\/(\d+)/);
  if (projectMatch) {
    const projectId = projectMatch[1];
    contextManager.updateContext('project_id', projectId, 'navigation');
    
    // Determine current stage based on URL
    const stageMapping: Record<string, string> = {
      '/setup': '1',
      '/context': '2', 
      '/cra': '3',
      '/adrs': '4',
      '/generation': '5',
      '/review': '6',
    };

    // Check which stage is in the URL
    for (const [stagePath, stageNumber] of Object.entries(stageMapping)) {
      if (pathname.includes(stagePath)) {
        contextManager.updateContext('current_stage', stageNumber, 'navigation');
        contextManager.updateContext('stage_name', stagePath.substring(1), 'navigation');
        break;
      }
    }
  } else {
    // Remove project context when not on a project page
    contextManager.removeContext('project_id');
    contextManager.removeContext('current_stage');
    contextManager.removeContext('stage_name');
  }
};

// Helper function to set project stage specific context
const setProjectStageContext = (pathname: string) => {
  if (pathname.includes('/setup')) {
    contextManager.updateContext('page_context', 'project_configuration', 'navigation');
  } else if (pathname.includes('/context')) {
    contextManager.updateContext('page_context', 'context_management', 'navigation');
  } else if (pathname.includes('/cra')) {
    contextManager.updateContext('page_context', 'context_review_analysis', 'navigation');
  } else if (pathname.includes('/adrs')) {
    contextManager.updateContext('page_context', 'architecture_decisions', 'navigation');
  } else if (pathname.includes('/generation')) {
    contextManager.updateContext('page_context', 'document_generation', 'navigation');
  } else if (pathname.includes('/review')) {
    contextManager.updateContext('page_context', 'final_review', 'navigation');
  }
};

// Helper function to set page-specific context
const setPageContext = (pathname: string) => {
  if (pathname === '/' || pathname === '/dashboard') {
    contextManager.updateContext('page_type', 'project_list', 'navigation');
    contextManager.updateContext('page_context', 'browsing_projects', 'navigation');
  } else if (pathname === '/create') {
    contextManager.updateContext('page_type', 'project_creation', 'navigation');
    contextManager.updateContext('page_context', 'creating_new_project', 'navigation');
  } else if (pathname === '/login') {
    contextManager.updateContext('page_type', 'authentication', 'navigation');
    contextManager.updateContext('page_context', 'user_login', 'navigation');
  } else if (pathname.includes('/project/')) {
    contextManager.updateContext('page_type', 'project_workflow', 'navigation');
    setProjectStageContext(pathname);
  }
};

/**
 * Hook to manage global context based on current page, user state, and navigation
 */
export const useGlobalContext = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Initialize context with user information when authentication changes
    if (isAuthenticated && user) {
      // Handle Entra ID user
      const userId = user.sub || 'unknown';
      const userLogin = user.username;
      
      contextManager.updateContext('user_id', userId, 'auth');
      contextManager.updateContext('user_login', userLogin, 'auth');
      if (user.name) {
        contextManager.updateContext('user_name', user.name, 'auth');
      }
      if (user.email) {
        contextManager.updateContext('user_email', user.email, 'auth');
      }
    } else {
      // Clear user context when not authenticated
      contextManager.removeContext('user_id');
      contextManager.removeContext('user_login');
      contextManager.removeContext('user_name');
      contextManager.removeContext('user_email');
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    // Update context based on current page and route
    const pathname = location.pathname;
    contextManager.updateContext('current_page', pathname, 'navigation');

    extractProjectContext(pathname);
    setPageContext(pathname);

    // Add timestamp for context freshness
    contextManager.updateContext('context_updated_at', new Date().toISOString(), 'system');
  }, [location.pathname]);

  return {
    // Expose context manager methods for components that need them
    updateContext: contextManager.updateContext.bind(contextManager),
    removeContext: contextManager.removeContext.bind(contextManager),
    clearContext: contextManager.clearContext.bind(contextManager),
    getContext: contextManager.getContext.bind(contextManager),
    getContextSummary: contextManager.getContextSummary.bind(contextManager),
  };
};
